import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Formik, Field, Form } from "formik";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as Yup from "yup";
import moment from "moment";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import CreatableSelect from "react-select/creatable";

const ProjectCreate = ({
  createProject,
  nextStep,
  clientProjectsResponseData,
  clientNameSearch,
  technologyResponseData,
  TechnologyNameSearch,
  projectInfoEdit,
  UpdateBasicProjectInfo,
  closeModal,
  directCloseModal,
  projectId,
}) => {
  const dispatch = useDispatch();

  const [client, setClient] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [clientName, setClientName] = useState(null);
  const [technologyList, setTechnologyList] = useState([]);
  const [technology, setTechnology] = useState([]);

  useEffect(() => {
    clientNameSearch();
    TechnologyNameSearch();
  }, []);

  useEffect(() => {
    if (clientProjectsResponseData) setClient(clientProjectsResponseData);
  }, [clientProjectsResponseData]);

  useEffect(() => {
    if (technologyResponseData) setTechnologyList(technologyResponseData);
  }, [technologyResponseData]);

  useEffect(() => {
    if (projectInfoEdit && (projectInfoEdit._id || projectInfoEdit.id)) {
      if (projectInfoEdit.start_date) {
        setStartDate(new Date(moment(projectInfoEdit.start_date).format("YYYY-MM-DD")));
      }
      if (projectInfoEdit.client) {
        if (typeof projectInfoEdit.client === "object") {
          setClientName({
            value: projectInfoEdit.client._id || projectInfoEdit.client.value || projectInfoEdit.client.id,
            label: projectInfoEdit.client.name || projectInfoEdit.client.label,
          });
        } else {
          setClientName({
            value: projectInfoEdit.client,
            label: projectInfoEdit.client,
          });
        }
      }

      // Extract raw technology entries from all possible backend structures
      let rawTechs = [];
      const p = projectInfoEdit;

      if (Array.isArray(p.clientTechDetails)) rawTechs.push(...p.clientTechDetails);
      if (Array.isArray(p.serverTechDetails)) rawTechs.push(...p.serverTechDetails);

      if (p.technologies && typeof p.technologies === "object" && !Array.isArray(p.technologies)) {
        if (Array.isArray(p.technologies.client)) rawTechs.push(...p.technologies.client);
        if (Array.isArray(p.technologies.server)) rawTechs.push(...p.technologies.server);
      }

      if (Array.isArray(p.technologies)) rawTechs.push(...p.technologies);
      if (Array.isArray(p.technology)) rawTechs.push(...p.technology);

      const isMongoId = (str) => typeof str === "string" && /^[0-9a-fA-F]{24}$/.test(str);

      const resolveTech = (item) => {
        let techId = "";
        let techName = "";
        let techType = "";

        if (typeof item === "string") {
          if (isMongoId(item)) {
            techId = item;
          } else {
            techName = item;
          }
        } else if (item && typeof item === "object") {
          techType = item.type || "";
          if (typeof item.techId === "object" && item.techId !== null) {
            techId = item.techId._id || item.techId.id || item.techId.value || "";
            techName = item.techId.name || item.techId.label || "";
          } else if (typeof item.techId === "string") {
            techId = item.techId;
          }

          if (!techName) {
            techName = item.name || item.label || "";
          }
          if (!techId) {
            techId = item._id || item.id || item.value || "";
          }
        }

        // Match against loaded technologyList
        let match = null;
        if (technologyList?.length > 0) {
          match = technologyList.find((t) => {
            if (techId && String(t._id) === String(techId)) return true;
            if (techId && String(t.id) === String(techId)) return true;
            if (techName && !isMongoId(techName) && String(t.name).toLowerCase() === String(techName).toLowerCase()) return true;
            return false;
          });
        }

        if (match) {
          return {
            value: match._id,
            label: match.name,
            type: techType || match.type || "web",
          };
        }

        // If techName is clean (not a Mongo hex ID)
        if (techName && !isMongoId(techName)) {
          return {
            value: techId || techName,
            label: techName,
            type: techType || "web",
          };
        }

        return null;
      };

      const formatted = [];
      const seen = new Set();

      rawTechs.forEach((item) => {
        const resolved = resolveTech(item);
        if (resolved && resolved.label && !seen.has(String(resolved.value))) {
          seen.add(String(resolved.value));
          formatted.push(resolved);
        }
      });

      setTechnology(formatted);
    }
  }, [projectInfoEdit, technologyList]);

  const isEditMode = Boolean(projectInfoEdit && (projectInfoEdit._id || projectInfoEdit.id));

  const parseInitialPlatform = (plat) => {
    if (!plat) return [];
    if (Array.isArray(plat)) return plat;
    const parts = String(plat)
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    return parts;
  };

  const togglePlatform = (currentPlatforms, target) => {
    let list = Array.isArray(currentPlatforms) ? [...currentPlatforms] : [];
    if (list.includes(target)) {
      return list.filter((item) => item !== target);
    } else {
      return [...list, target];
    }
  };

  // Custom react-select styles matching modern slate UI
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "0.75rem",
      borderColor: state.isFocused ? "#0f172a" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(15, 23, 42, 0.08)" : "none",
      padding: "2px 4px",
      fontSize: "0.875rem",
      "&:hover": {
        borderColor: "#cbd5e1",
      },
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#f1f5f9",
      borderRadius: "0.5rem",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#0f172a",
      fontSize: "0.75rem",
      fontWeight: "600",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#64748b",
      "&:hover": {
        backgroundColor: "#e2e8f0",
        color: "#0f172a",
      },
    }),
  };

  return (
    <div className="w-full">
      {/* Header & Stepper */}
      <div className="mb-6">
        <div className="flex items-center justify-between gap-4 mb-2">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              {isEditMode ? "Edit Project Details" : "Create New Project"}
            </h2>
            <p className="text-xs text-slate-500 font-medium">
              {isEditMode
                ? "Update essential project information"
                : "Enter core details to initialize project"}
            </p>
          </div>

          {!isEditMode && (
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center shadow-xs">
                1
              </span>
              <span className="text-xs font-semibold text-slate-400 hidden sm:inline">
                of 2 steps
              </span>
            </div>
          )}
        </div>

        {!isEditMode && (
          <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden mt-3">
            <div className="bg-slate-900 h-full w-1/2 rounded-full transition-all duration-300"></div>
          </div>
        )}
      </div>

      <Formik
        initialValues={{
          name: projectInfoEdit?.name || "",
          client: projectInfoEdit?.client?._id || "",
          platform: parseInitialPlatform(projectInfoEdit?.platform),
          startDate: projectInfoEdit?.start_date || "",
          technology: [],
          technologyCheck: isEditMode ? "valid" : "",
        }}
        validateOnBlur={false}
        validationSchema={Yup.object().shape({
          name: Yup.string().required("Project name is required"),
          platform: Yup.array().min(1, "Select at least one platform type"),
          startDate: Yup.string().required("Start date is required").nullable(),
          technologyCheck: isEditMode
            ? null
            : Yup.string().required("Select at least one technology").nullable(),
        })}
        onSubmit={async (values, { resetForm }) => {
          const formattedPlatform = Array.isArray(values.platform)
            ? values.platform.join(",")
            : values.platform;

          if (isEditMode) {
            const pId = projectInfoEdit._id || projectInfoEdit.id;
            const payload = {
              name: values.name,
              platform: formattedPlatform,
              start_date: values.startDate ? moment(values.startDate).format("YYYY-MM-DD") : undefined,
              client: clientName
                ? typeof clientName === "object"
                  ? {
                      _id: clientName.value,
                      name: clientName.label,
                      label: clientName.label,
                    }
                  : clientName
                : null,
              technologies:
                technology?.length > 0
                  ? technology.map((item) => ({
                      _id: item.value,
                      techId: item.value,
                      type: item.type?.length > 0 ? item.type : "web",
                      name: item.label,
                    }))
                  : [],
              project_id: pId,
            };
            try {
              const res = await UpdateBasicProjectInfo(payload);
              if (res) {
                toast.success("Project updated successfully");
                setClientName(null);
                setStartDate(null);
                setTechnology([]);
                resetForm();
                if (directCloseModal) {
                  directCloseModal();
                } else if (closeModal) {
                  closeModal(true);
                }
                dispatch.projects.listProjectData({ currentPage: 0, limit: 12 });
              }
            } catch (err) {
              toast.error(err?.error || err?.response?.data?.message || err?.message || "Failed to update project");
            }
          } else {
            const payload = {
              name: values.name,
              platform: formattedPlatform,
              start_date: moment(values.startDate).format("YYYY-MM-DD"),
              technologies:
                technology?.length > 0
                  ? technology.map((item) => ({
                      _id: item.value,
                      techId: item.value,
                      type: item.type?.length > 0 ? item.type : "",
                      name: item.label,
                    }))
                  : [],
              client: clientName,
            };

            try {
              const res = await createProject(payload);
              if (res?.message) toast.success(res.message);
              if (res?.status === 200) {
                setClientName(null);
                setStartDate(null);
                setTechnology([]);
                resetForm();
                nextStep(false);
                dispatch.projects.listProjectData({ currentPage: 0, limit: 12 });
              }
            } catch (err) {
              toast.error(err?.error || "Failed to create project");
            }
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          submitCount,
        }) => {
          const isWebSelected = Array.isArray(values.platform)
            ? values.platform.includes("web")
            : String(values.platform).includes("web");

          const isMobileSelected = Array.isArray(values.platform)
            ? values.platform.includes("mobile")
            : String(values.platform).includes("mobile");

          const nameError = errors.name && (touched.name || submitCount > 0);
          const platformError = errors.platform && (touched.platform || submitCount > 0);
          const startDateError = errors.startDate && (touched.startDate || submitCount > 0);
          const techError = errors.technologyCheck && (touched.technologyCheck || submitCount > 0);

          return (
            <Form onSubmit={handleSubmit} className="space-y-4">
              {/* Project Name */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 font-mono">
                  Project Name <span className="text-rose-500">*</span>
                </label>
                <Field
                  className={`w-full px-4 py-2.5 rounded-xl border text-sm font-medium focus:outline-none transition-all placeholder:text-slate-400 bg-white ${
                    nameError ? 'border-rose-500 ring-2 ring-rose-500/20' : 'border-slate-200 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10'
                  }`}
                  placeholder="e.g. Acme Mobile App"
                  type="text"
                  name="name"
                  onChange={handleChange}
                  value={values.name}
                />
                {nameError && (
                  <span className="text-rose-500 text-xs font-semibold mt-1 block">
                    {errors.name}
                  </span>
                )}
              </div>

              {/* Multi-Select Platform Type Cards */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 font-mono">
                    Platform Type <span className="text-rose-500">*</span>
                  </label>
                  <span className="text-[11px] font-medium text-slate-400">
                    Select all that apply
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3.5">
                  {/* Web App Option Card */}
                  <div
                    onClick={() =>
                      setFieldValue("platform", togglePlatform(values.platform, "web"))
                    }
                    className={`p-3.5 border rounded-2xl cursor-pointer select-none flex items-center justify-center gap-3 transition-all ${
                      isWebSelected
                        ? "border-slate-900 bg-white ring-2 ring-slate-900/10 shadow-2xs"
                        : platformError
                        ? "border-rose-300 bg-rose-50/20"
                        : "border-slate-200/90 hover:border-slate-300 bg-white hover:bg-slate-50/50"
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors shrink-0 ${
                        isWebSelected
                          ? "bg-slate-900 text-white shadow-2xs"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                      </svg>
                    </div>
                    <span className="text-xs font-bold text-slate-900">
                      Web App
                    </span>
                  </div>

                  {/* Mobile App Option Card */}
                  <div
                    onClick={() =>
                      setFieldValue("platform", togglePlatform(values.platform, "mobile"))
                    }
                    className={`p-3.5 border rounded-2xl cursor-pointer select-none flex items-center justify-center gap-3 transition-all ${
                      isMobileSelected
                        ? "border-slate-900 bg-white ring-2 ring-slate-900/10 shadow-2xs"
                        : platformError
                        ? "border-rose-300 bg-rose-50/20"
                        : "border-slate-200/90 hover:border-slate-300 bg-white hover:bg-slate-50/50"
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors shrink-0 ${
                        isMobileSelected
                          ? "bg-slate-900 text-white shadow-2xs"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                        <line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="3" />
                      </svg>
                    </div>
                    <span className="text-xs font-bold text-slate-900">
                      Mobile App
                    </span>
                  </div>
                </div>
                {platformError && (
                  <span className="text-rose-500 text-xs font-semibold mt-1 block">
                    {errors.platform}
                  </span>
                )}
              </div>

              {/* Client Selection & Start Date in 2-column grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Client Name */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 font-mono">
                    Client
                  </label>
                  <CreatableSelect
                    styles={selectStyles}
                    value={clientName}
                    isSearchable={true}
                    isClearable={true}
                    options={client.map((c) => ({
                      label: c.name,
                      value: c._id,
                    }))}
                    placeholder="Select or enter client..."
                    onChange={(e) => setClientName(e)}
                  />
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 font-mono">
                    Start Date <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <DatePicker
                      className={`w-full px-4 py-2.5 rounded-xl border text-sm font-medium focus:outline-none transition-all placeholder:text-slate-400 bg-white ${
                        startDateError ? 'border-rose-500 ring-2 ring-rose-500/20' : 'border-slate-200 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10'
                      }`}
                      name="startDate"
                      placeholderText="Select start date"
                      selected={startDate}
                      dateFormat="dd-MM-yyyy"
                      onChange={(date) => {
                        setStartDate(date);
                        setFieldValue("startDate", date);
                      }}
                    />
                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  </div>
                  {startDateError && (
                    <span className="text-rose-500 text-xs font-semibold mt-1 block">
                      {errors.startDate}
                    </span>
                  )}
                </div>
              </div>

              {/* Technologies */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 font-mono">
                  Technologies {!isEditMode && <span className="text-rose-500">*</span>}
                </label>
                <Select
                  styles={{
                    ...selectStyles,
                    control: (base, state) => ({
                      ...selectStyles.control(base, state),
                      borderColor: techError ? '#f43f5e' : state.isFocused ? '#0f172a' : '#e2e8f0',
                      boxShadow: techError ? '0 0 0 2px rgba(244, 63, 94, 0.2)' : state.isFocused ? '0 0 0 2px rgba(15, 23, 42, 0.08)' : 'none',
                    })
                  }}
                  name="technology"
                  value={technology}
                  isMulti
                  isSearchable={true}
                  isClearable={true}
                  options={technologyList.map((tech) => ({
                    label: tech.name,
                    value: tech._id,
                    type: tech.type,
                  }))}
                  placeholder="Select tech stack (React, Node, etc)..."
                  onChange={(option) => {
                    if (option?.length > 0) {
                      setTechnology(option);
                      setFieldValue("technologyCheck", option[0].label);
                    } else {
                      setTechnology([]);
                      setFieldValue("technologyCheck", isEditMode ? "valid" : "");
                    }
                  }}
                />
                {techError && (
                  <span className="text-rose-500 text-xs font-semibold mt-1 block">
                    {errors.technologyCheck}
                  </span>
                )}
              </div>

              {/* Actions Bar */}
              <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2.5 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={isSubmitting}
                  type="submit"
                  className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition-all shadow-2xs flex items-center gap-2"
                >
                  <span>{isEditMode ? "Update Project" : "Next Step"}</span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                    />
                  </svg>
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

const mapState = (state) => ({
  clientProjectsResponseData: state.projects.projectClient,
  technologyResponseData: state.projects.technology,
});

const mapDispatch = (dispatch) => ({
  createProject: dispatch.projects.createProject,
  clientNameSearch: dispatch.projects.clientNameSearch,
  TechnologyNameSearch: dispatch.projects.technologyNameSearch,
  UpdateBasicProjectInfo: dispatch.projectDetails.UpdateBasicProjectInfo,
});

export default connect(mapState, mapDispatch)(ProjectCreate);
